import type { ComponentChildren } from 'preact'
import { useState, useRef, useEffect } from 'preact/hooks'
import { ChevronDown } from 'lucide-preact'

interface AccordionProps {
  children: ComponentChildren
  title: string
  className?: string
  defaultOpen?: boolean
  buttonClassName?: string
  contentClassName?: string
  scrollOnOpen?: boolean
  scrollOffset?: number
}

export function Accordion({
  children,
  title,
  className = '',
  defaultOpen = false,
  buttonClassName = '',
  contentClassName = '',
  scrollOnOpen = true,
  scrollOffset = 16,
}: AccordionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen)
  const accordionRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isOpen && scrollOnOpen && accordionRef.current) {
      const element = accordionRef.current
      const rect = element.getBoundingClientRect()
      const isPartiallyVisible =
        rect.top < scrollOffset || rect.bottom > window.innerHeight

      if (isPartiallyVisible) {
        const prefersReducedMotion = window.matchMedia(
          '(prefers-reduced-motion: reduce)'
        ).matches

        element.scrollIntoView({
          behavior: prefersReducedMotion ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'nearest',
        })
      }
    }
  }, [isOpen, scrollOnOpen, scrollOffset])

  const toggleOpen = () => setIsOpen(!isOpen)

  return (
    <div
      ref={accordionRef}
      className={`border border-gray-200 rounded-lg ${className}`}
    >
      <button
        onClick={toggleOpen}
        className={`w-full px-4 py-3 text-left bg-cencert-bg-gray transition-colors flex items-center justify-between ${buttonClassName}`}
        aria-expanded={isOpen}
        type='button'
      >
        <span className='font-medium text-gray-900'>{title}</span>
        <ChevronDown
          size={20}
          className={`text-gray-500 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      {isOpen && (
        <div className={`p-4 border-t border-gray-200 ${contentClassName}`}>
          {children}
        </div>
      )}
    </div>
  )
}
